import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../supabaseClient";
import { sanitizePhone, sanitizeInput, sanitizeUsbcId } from "../utils/sanitization";

export const useProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadProfile = async () => {
    if (!user) {
      setProfile(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      if (error) {
        // If profile doesn't exist, create it
        if (error.code === "PGRST116") {
          try {
            // Use a server-side function to safely migrate user data
            const { data: result, error: createError } = await supabase.rpc(
              "create_user_profile",
              { user_id: user.id }
            );

            if (createError) throw createError;

            if (result.success) {
              setProfile(result.profile);
            } else {
              throw new Error(result.error || "Failed to create profile");
            }
          } catch (functionError) {
            console.warn(
              "Function failed, creating profile manually:",
              functionError
            );
            // Fallback: create profile manually if function fails
            const { data: newProfile, error: createError } = await supabase
              .from("profiles")
              .insert({
                id: user.id,
                full_name: "",
                phone_number: "",
              })
              .select()
              .single();

            if (createError) throw createError;
            setProfile(newProfile);
          }
        } else {
          throw error;
        }
      } else {
        setProfile(data);
      }
    } catch (err) {
      console.error("Error loading profile:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateProfile = async (updates) => {
    if (!user) return { error: "No user logged in" };

    // Validate and sanitize input
    const sanitizedUpdates = {};

    if (updates.full_name !== undefined) {
      // Sanitize full_name: trim, limit length, remove HTML
      sanitizedUpdates.full_name = updates.full_name
        ? updates.full_name
            .trim()
            .substring(0, 100)
            .replace(/<[^>]*>/g, "")
        : null;
    }

    if (updates.phone_number !== undefined) {
      // Sanitize phone_number: use standard sanitizePhone() to store as digits only
      // This ensures consistency across all profile updates
      sanitizedUpdates.phone_number = updates.phone_number
        ? sanitizePhone(updates.phone_number)
        : null;
    }

    if (updates.usbcId !== undefined) {
      // Sanitize usbcId: only digits, limit to 15 digits
      sanitizedUpdates.usbcId = updates.usbcId ? sanitizeUsbcId(updates.usbcId) : null;
    }

    try {
      const { data, error } = await supabase
        .from("profiles")
        .update(sanitizedUpdates)
        .eq("id", user.id)
        .select()
        .single();

      if (error) throw error;

      setProfile(data);
      return { data, error: null };
    } catch (err) {
      console.error("Error updating profile:", err);
      return { data: null, error: err.message };
    }
  };

  useEffect(() => {
    loadProfile();
  }, [user]);

  return {
    profile,
    loading,
    error,
    updateProfile,
    refreshProfile: loadProfile,
  };
};
